import { Injectable, inject } from '@angular/core';
import { TreeNode } from 'primeng/api';
import { BehaviorSubject, Observable } from 'rxjs';

import { HisTreeService } from '../../tree/service/his-tree.service';
import { HisOption } from '../model/his-option';
import { HisSearchConfiguration, HisSearchConfigurationCriterion } from '../model/his-search-configuration';
import { HisWidgetBase } from '../model/his-widget-base';
import { HisCalendarWidget } from '../model/his-widget-calendar';
import { HisWidgetGroup } from '../model/his-widget-group';
import { HisInputWidget } from '../model/his-widget-input';
import { HisMultiSelectWidget } from '../model/his-widget-multiselect';
import { HisOptionWidget } from '../model/his-widget-option';
import { HisSearchSelectWidget } from '../model/his-widget-search-select';
import { HisSelectWidget } from '../model/his-widget-select';
import { HisTreeSelectWidget } from '../model/his-widget-tree-select';
import { HisSearchService } from './his-search.service';

import * as _ from 'lodash';
import { GenericSearchUtil } from '../utils/generic-search-util';

@Injectable({providedIn: 'root'})
export class HisWidgetService {
    private hisSearchService = inject(HisSearchService);
    private hisTreeService = inject(HisTreeService);


    // cached select options for dropdowns etc.
    private selectFromOptionsCache: Map<string, BehaviorSubject<HisOption[]>> = new Map();
    private loadedSelectFromOptions: Set<string> = new Set();

    /**
     * Create all widgets and widget groups for a search page.
     * (All really means all, even if we are in "simple search" mode.)
     *
     * @param searchConfig search configuration
     * @param an array of all widgetGroups
     */
    getWidgetGroups( searchConfig: HisSearchConfiguration ): HisWidgetGroup[] {
        let widgetGroups: HisWidgetGroup[] = [];

        for ( let searchGroup of searchConfig.searchCriteria.group ) {
            let label = searchGroup.label;
            let helptext = searchGroup.helptext;

            let widgetGroup = new HisWidgetGroup( { label, helptext } );

            let index = 0;
            for ( let searchCriterion of searchGroup.criteria ) {
                index++;
                let widget = this.getWidget( widgetGroup, searchCriterion, index, null, searchConfig.configFile );
                // console.log("HisWidgetService.getWidgetGroups(): Created widget", widget.id);
                widgetGroup.widgets.push( widget );
            }
            widgetGroup.widgets.sort( ( w1: HisWidgetBase<any>, w2: HisWidgetBase<any> ) => w1.order - w2.order );

            widgetGroup.quickSearch = false;
            for ( let widget of widgetGroup.widgets ) {
                if ( widget.quickSearch ) {
                    widgetGroup.quickSearch = true;
                    break;
                }
            }

            widgetGroup.criteriaGroup = searchGroup.criteriaGroup;

            widgetGroups.push( widgetGroup );
        }

        return widgetGroups;
    }

    /**
     * Gets the value from searchCriterion if value != null
     * in case of loading criteria via search-criteria-set.
     *
     * @param searchCriterion a HisSearchCriterion
     * @param model the model
     */
    private getValue( searchCriterion: HisSearchConfigurationCriterion, model?: any ): any {
        if ( searchCriterion.value != null ) {
            return searchCriterion.value;
        }
        if ( searchCriterion.defaultValue != null ) {
            console.log('#searchCriterion.defaultValue', searchCriterion.defaultValue);
            return searchCriterion.defaultValue;
        }
        return ( model ) ? model[searchCriterion.path] : null;
    }

    private getWidget( widgetGroup: HisWidgetGroup, searchCriterion: HisSearchConfigurationCriterion,
        index: number, model?: any, configFile?: string ): HisWidgetBase<any> {
        let kind: string;
        if ( searchCriterion.selectFrom ) {
            kind = ( searchCriterion.multipleSelect ) ? 'multi-dropdown' : 'dropdown';
        } else if ( !_.isNil( searchCriterion.value ) ) {
            // XXX does this make sense? A value attribute can have more options than 0 and 1 ...
            kind = 'checkbox';
        } else if ( searchCriterion.treeName ) {
            kind = 'tree-select';
        } else if ( searchCriterion.searchConfigFile ) {
            kind = 'search-select';
        } else if ( searchCriterion.inputType && searchCriterion.inputType.toUpperCase() === 'DATE' ) {
            kind = 'calendar';
        } else {
            kind = 'input';
        }

        let id = searchCriterion.path;
        let options: any = {
            group: widgetGroup.id,
            id,
            kind,
            label: searchCriterion.label,
            helptext: searchCriterion.helptext,
            value: this.getValue( searchCriterion, model ),
            order: index,
            quickSearch: searchCriterion.quickSearchCriterion,
            required: searchCriterion.required,
            inputType: searchCriterion.inputType
        };

        // console.log('Creating widget', id, 'of kind', kind, 'with options', options);

        let widget: HisWidgetBase<any>;

        switch ( kind ) {
            case 'dropdown':
            case 'multi-dropdown': {
                options.multiple = searchCriterion.multipleSelect;
                let selectFrom = searchCriterion.selectFrom;
                options.optionsKey = selectFrom;
                // Get or create an observable for the select options of the widget
                options.options = this.getSelectOptions( selectFrom );

                options.showNullItem = searchCriterion.showNullItem;
                options.showDropdownIcon = searchCriterion.showDropdownIcon;
                options.minLength = searchCriterion.typeaheadMinLength || 1;
                options.limit = searchCriterion.typeaheadMaxResults || null;
                options.showUnequalOption = searchCriterion.showUnequalOption;
                options.not = searchCriterion.not;
                options.configFile = configFile;

                if ( kind === 'dropdown' ) {
                    let selectWidget = new HisSelectWidget( options );
                    if ( id.endsWith( '(NOT)' ) ) {
                        selectWidget.not = true;
                    }
                    widget = selectWidget;
                } else { // multi-dropdown
                    let multiWidget = new HisMultiSelectWidget( options );
                    if ( id.endsWith( '(NOT)' ) ) {
                        multiWidget.not = true;
                    }
                    widget = multiWidget;
                }

                break;
            }
            case 'tree-select': {
                console.log( 'HisWidgetService.getWidget(): selectFromTree = ' + searchCriterion.selectFromTree );
                let tree: BehaviorSubject<TreeNode[]>
                    = this.hisTreeService.loadTree( searchCriterion.treeName, searchCriterion.selectFromTree );
                // TODO load tree only if the component is really rendered

                options.showNullItem = searchCriterion.showNullItem;
                options.showDropdownIcon = searchCriterion.showDropdownIcon;
                options.minLength = searchCriterion.typeaheadMinLength || 1;
                options.limit = searchCriterion.typeaheadMaxResults || null;
                options.showUnequalOption = searchCriterion.showUnequalOption;
                options.not = searchCriterion.not;

                let treeWidget = new HisTreeSelectWidget( options, searchCriterion.treeName, tree );
                if ( id.endsWith( '(NOT)' ) ) {
                    treeWidget.not = true;
                }
                widget = treeWidget;

                break;
            }
            case 'search-select': {
                options.searchEntity = searchCriterion.searchEntity;
                options.searchConfigFile = searchCriterion.searchConfigFile;
                options.selectedOptionsIdColumn = searchCriterion.searchEntity + '.id';

                // Name der Spalte der Suchergebnistabelle, die für die Labels ausgewählter Elemente genommen wird:
                // Momentan setzen wir diese auf undefined, woraufhin in his-search-result.component einfach
                // die erste Spalte genommen wird, was in den meisten Fällen funktionieren sollte.
                // searchCriterion.searchEntity+".defaulttext" zu nehmen wäre zu unflexibel und würde beispielsweise
                // die Labels aus Publication.title nicht finden.
                // TODO: Wenn die aktuelle Lösung nicht ausreicht, muss der Name der Label-Spalte aus
                // der Suchkonfigurationsdatei gelesen werden.
                options.selectedOptionsLabelColumn = undefined;

                options.showNullItem = searchCriterion.showNullItem;
                options.showDropdownIcon = searchCriterion.showDropdownIcon;
                options.minLength = searchCriterion.typeaheadMinLength || 1;
                options.limit = searchCriterion.typeaheadMaxResults || null;
                options.showUnequalOption = searchCriterion.showUnequalOption;
                options.not = searchCriterion.not;
                options.showSearchFilter = searchCriterion.showSearchFilter;

                // options for autocomplete in his-search-select are loaded in the component,
                // filtered by the autocomplete input

                let searchSelectWidget = new HisSearchSelectWidget( this.hisSearchService, options );
                if ( id.endsWith( '(NOT)' ) ) {
                    searchSelectWidget.not = true;
                }
                widget = searchSelectWidget;

                break;
            }
            case 'checkbox': {
                options.value = searchCriterion.defaultValue === searchCriterion.value; // analog to JSF: preset as true when default value is exactly the value (not just truthy)
                options.option = searchCriterion.value;
                widget = new HisOptionWidget( options );

                break;
            }
            case 'calendar': {
                widget = new HisCalendarWidget( options );

                break;
            }
            default: {
                console.log('WidgetService: multiSearchCriterion: ', searchCriterion.multiSearchCriterion);
                if(searchCriterion.multiSearchCriterion) {
                    options.multiSearchCriterion = searchCriterion.multiSearchCriterion;
                }
                console.log('WidgetService: multiSearchCriterionLabels: ', searchCriterion.multiSearchCriterionLabels);
                if(searchCriterion.multiSearchCriterionLabels) {
                    options.multiSearchCriterionLabels = searchCriterion.multiSearchCriterionLabels;
                }
                if (searchCriterion.placeholder) {
                    options.placeholder = searchCriterion.placeholder;
                }
                widget = new HisInputWidget( options );

                break;
            }
        }

        return widget;
    }

    /**
     * Load all widget options for a search page.
     * For performance reasons we want to do only one backend call for a whole search page.
     *
     * @param searchConfig search configuration
     * @param showAllSearchCriteria if true then we are in extended search state
     */
    public loadWidgetOptions( searchConfig: HisSearchConfiguration, showAllSearchCriteria: boolean ) {
        // Scan the complete search configuration for widgets needing select options
        let keys: string[] = [];
        for ( let group of searchConfig.searchCriteria.group ) {
            for ( let criterion of group.criteria ) {
                let key = criterion.selectFrom;
                if ( key && ( showAllSearchCriteria || criterion.quickSearchCriterion ) && !this.loadedSelectFromOptions.has( key )
                    && !GenericSearchUtil.isLazySelectFrom( criterion.selectFrom, criterion.showDropdownIcon ) ) {
                    // The widget needs select options, it must be displayed, and the options have not been loaded yet -> mark to load
                    keys.push( key );
                }
            }
        }

        if ( keys.length > 0 ) {
            // Backend call required, to get the data:
            let data: Map<string, Observable<HisOption[]>> = this.hisSearchService.loadDataForSearchForm( searchConfig, keys );
            data.forEach( ( options: Observable<HisOption[]>, key: string ) => {
                // pass loaded options from input observable to output subject
                options.subscribe( ( opts: HisOption[] ) => {
                    this.getSelectOptions( key ).next( opts );

                    // XXX das war im merge nicht mehr drin
                    this.loadedSelectFromOptions.add( key );
                } );
            } );
        }
    }

    getSelectOptions( key: string ): BehaviorSubject<HisOption[]> {
        if ( !this.selectFromOptionsCache.has( key ) ) {
            let subject: BehaviorSubject<HisOption[]> = new BehaviorSubject( [] );
            this.selectFromOptionsCache.set( key, subject );
        }
        return this.selectFromOptionsCache.get( key );
    }

    copyWidget( original: HisWidgetBase<any>, newId: string ): HisWidgetBase<any> {
        //        console.log('copy widget of typet: ' + original.kind);

        let options: any = {
            group: original.group,
            id: newId,
            kind: original.kind,
            label: original.label,
            helptext: original.helptext,
            order: original.order,
            quickSearch: original.quickSearch,
            required: original.required,
            inputType: original.inputType,
            value: original.value
        };
        console.log( 'his-widget.service#copyWidget()', '#options', options );

        switch ( original.kind ) {
            case 'dropdown': {
                let originalWidget = original as HisSelectWidget;
                options.multiple = originalWidget.multiple;
                options.optionsKey = originalWidget.optionsKey;
                options.options = originalWidget.options;
                options.showNullItem = originalWidget.showNullItem;
                options.showDropdownIcon = originalWidget.showDropdownIcon;
                options.minLength = originalWidget.minLength;
                options.limit = originalWidget.limit;
                options.not = originalWidget.not;
                options.configFile = originalWidget.configFile;

                return new HisSelectWidget( options );
            }
            case 'multi-dropdown': {
                let originalWidget = original as HisMultiSelectWidget;
                options.multiple = originalWidget.multiple;
                options.optionsKey = originalWidget.optionsKey;
                options.options = originalWidget.options;
                options.showNullItem = originalWidget.showNullItem;
                options.showDropdownIcon = originalWidget.showDropdownIcon;
                options.minLength = originalWidget.minLength;
                options.limit = originalWidget.limit;
                options.not = originalWidget.not;

                return new HisMultiSelectWidget( options );
            }
            case 'tree-select': {
                let originalWidget = original as HisTreeSelectWidget;
                options.showNullItem = originalWidget.showNullItem;
                options.showDropdownIcon = originalWidget.showDropdownIcon;
                options.minLength = originalWidget.minLength;
                options.limit = originalWidget.limit;
                options.not = originalWidget.not;

                let treeName = originalWidget.treeName;
                let treeSubject = originalWidget.treeSubject;

                return new HisTreeSelectWidget( options, treeName, treeSubject );
            }
            case 'search-select': {
                let originalWidget = original as HisSearchSelectWidget;
                options.searchEntity = originalWidget.searchEntity;
                options.searchConfigFile = originalWidget.searchConfigFile;
                options.selectedOptionsIdColumn = originalWidget.selectedOptionsIdColumn;
                options.selectedOptionsLabelColumn = originalWidget.selectedOptionsLabelColumn;
                options.showNullItem = originalWidget.showNullItem;
                options.showDropdownIcon = originalWidget.showDropdownIcon;
                options.minLength = originalWidget.minLength;
                options.limit = originalWidget.limit;
                options.not = originalWidget.not;
                options.showSearchFilter = originalWidget.showSearchFilter;

                return new HisSearchSelectWidget( this.hisSearchService, options );
            }
            case 'checkbox': {
                let originalWidget = original as HisOptionWidget;
                options.option = originalWidget.option;
                return new HisOptionWidget( options );
            }
            case 'calendar': {
                return new HisCalendarWidget( options );
            }
            default: {
                return new HisInputWidget( options );
            }
        }
    }
}
